import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Maximize, Minimize, Volume2, VolumeX, ArrowLeft, Pause, Coffee, Info } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';

function fmt(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

// Автомасштабирование шрифта блайндов: подбираем размер, чтобы весь ряд
// (SB + divider + BB) уместился в доступную ширину контейнера, не пересекая
// правую панель. С боковой панелью: 500|1000 ≈9cqw, миллионы ≈5cqw.
function computeBlindsFontSize(sb, bb, hasSideInfo) {
  const sbFmt = (sb || 0).toLocaleString('ru-RU');
  const bbFmt = (bb || 0).toLocaleString('ru-RU');
  const totalChars = sbFmt.length + bbFmt.length;
  const containerCqw = hasSideInfo ? 47.25 : 62;
  const pad = hasSideInfo ? 1.89 : 1.0;
  const maxCqw = containerCqw / (totalChars * 0.42 + pad);
  const size = Math.min(14, Math.max(4.5, maxCqw));
  return `${size.toFixed(2)}cqw`;
}
function computeNextBlindsFontSize(sb, bb) {
  const sbFmt = (sb || 0).toLocaleString('ru-RU');
  const bbFmt = (bb || 0).toLocaleString('ru-RU');
  const totalChars = sbFmt.length + bbFmt.length;
  // Нижний блок «Следующие блайнды» занимает ~30% ширины экрана (1/3 колонка).
  const maxCqw = 28 / (totalChars * 0.5 + 1.2);
  const size = Math.min(4, Math.max(1.5, maxCqw));
  return `${size.toFixed(2)}cqw`;
}

// Рендер каждой цифры в фиксированной ячейке — таймер не «прыгает» по бокам.
// Отдельные ширины: цифры плотнее, ":" сидит ближе к цифрам (узкая ячейка).
export function StaticDigits({ text, digitWidth = '0.52em', colonWidth = '0.26em' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, letterSpacing: 0 }}>
      {text.split('').map((ch, i) => {
        if (ch === ':') {
          return (
            <span
              key={i}
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: colonWidth,
                height: '0.85em',
                gap: '0.16em',
                // Oswald digits имеют низкий оптический центр (базовая линия ниже
                // геометрической середины em-бокса), поэтому сдвигаем двоеточие
                // немного вниз, чтобы визуально совпадало с центром цифр.
                transform: 'translateY(0.06em)'
              }}
            >
              <span style={{ display: 'block', width: '0.14em', height: '0.14em', background: 'currentColor' }} />
              <span style={{ display: 'block', width: '0.14em', height: '0.14em', background: 'currentColor' }} />
            </span>
          );
        }
        return (
          <span
            key={i}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: digitWidth,
              height: '0.85em',
              textAlign: 'center'
            }}
          >
            {ch}
          </span>
        );
      })}
    </span>
  );
}

// Вертикальная палочка-разделитель, центрированная по высоте текста.
function VDivider({ height = '0.7em', thickness = '0.08em', color = 'currentColor', opacity = 0.85 }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: thickness,
        minWidth: thickness,
        height,
        flexShrink: 0,
        background: color,
        opacity,
        borderRadius: '0.02em',
        verticalAlign: 'middle'
      }}
    />
  );
}

function fmtLong(sec) {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export function computeRemaining(t, nowMs) {
  if (!t) return 0;
  if (!t.isRunning || t.isPaused) return Math.max(0, t.timeRemainingSec || 0);
  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (t.endsAt) return Math.max(0, Math.round((t.endsAt - now) / 1000));
  return Math.max(0, t.timeRemainingSec || 0);
}

// Пользовательские звуки: играем при пересечении настраиваемого «за N секунд до конца».
// Для обхода autoplay-политики браузеров TV мы держим ОДИН <Audio>-элемент,
// разблокированный первой пользовательской gesture-интеракцией. Ниже в
// компоненте кладём его в ref и вызываем `playViaUnlocked(dataUrl)`.
function playAudioSafe(dataUrl) {
  try {
    const a = new Audio(dataUrl);
    a.volume = 1.0;
    const p = a.play();
    if (p && typeof p.catch === 'function') p.catch(() => { /* autoplay policy */ });
  } catch (e) { /* noop */ }
}

function secondsUntilBreak(t, currentRemaining) {
  if (!t) return null;
  const cur = t.blinds[t.currentLevelIndex];
  if (!cur) return null;
  if (cur.isBreak) return 0;
  let acc = currentRemaining || 0;
  for (let i = t.currentLevelIndex + 1; i < t.blinds.length; i++) {
    if (t.blinds[i].isBreak) return acc;
    acc += (t.blinds[i].duration || 0) * 60;
  }
  return null;
}

export default function ClockView() {
  const { code } = useParams();
  const nav = useNavigate();
  const { getByCode, soundEnabled, setSound, fetchByCode, fetchClockAssets, subscribeCode, getServerNow } = useApp();
  const t = getByCode(code);
  const [fs, setFs] = React.useState(false);
  const [, setTick] = React.useState(0);
  const [notFound, setNotFound] = React.useState(false);

  // Загрузка + подписка на WebSocket. `/api/clock/{code}` возвращает LITE
  // состояние (~3KB), TV рендерится за 200мс. Затем асинхронно догружается
  // тяжёлый backgroundImage — на медленном RU-канале это уже не блокирует UI.
  React.useEffect(() => {
    let alive = true;
    fetchByCode(code).then((data) => {
      if (!alive) return;
      if (!data) { setNotFound(true); return; }
      // Асинхронно догружаем blob'ы. Ошибки игнорим — часы работают и без bg.
      fetchClockAssets && fetchClockAssets(code).catch(() => {});
    });
    const unsub = subscribeCode(code);
    return () => { alive = false; unsub && unsub(); };
  }, [code, fetchByCode, fetchClockAssets, subscribeCode]);

  // Локальный тик — только для перерисовки, без записи в state.
  React.useEffect(() => {
    const i = setInterval(() => setTick((n) => (n + 1) % 1e6), 250);
    return () => clearInterval(i);
  }, []);

  const remaining = computeRemaining(t, getServerNow ? getServerNow() : undefined);

  // Проигрывание пользовательских звуков за настраиваемое время до конца уровня
  const playedSoundsRef = React.useRef(new Set());
  const lastLevelIdxRef = React.useRef(t?.currentLevelIndex);
  React.useEffect(() => {
    // При смене уровня сбрасываем «уже сыгранные» звуки
    if (t && lastLevelIdxRef.current !== t.currentLevelIndex) {
      lastLevelIdxRef.current = t.currentLevelIndex;
      playedSoundsRef.current = new Set();
    }
  }, [t?.currentLevelIndex, t]);

  // --- Audio unlock: браузеры (Chrome/Safari/TV WebViews) блокируют
  //     autoplay Audio до первой user gesture. Держим один HTMLAudioElement,
  //     разблокированный кликом/тачем/клавишей — потом переиспользуем его
  //     для проигрывания любых звуков. Без этого на телевизоре звук молчал,
  //     а на компе (где юзер уже кликал) — играл.
  const unlockedAudioRef = React.useRef(null);
  const [audioUnlocked, setAudioUnlocked] = React.useState(false);

  const unlockAudio = React.useCallback(() => {
    if (unlockedAudioRef.current) { setAudioUnlocked(true); return; }
    try {
      const a = new Audio();
      // Кратчайший валидный silent WAV (44 sample). Играем на нулевой
      // громкости — цель только «разбудить» аудио-контекст вкладки.
      a.src = 'data:audio/wav;base64,UklGRi4AAABXQVZFZm10IBAAAAABAAEAgD4AAIA+AAABAAgAZGF0YQoAAACAgICAgICAgIA=';
      a.volume = 0;
      const p = a.play();
      if (p && typeof p.then === 'function') {
        p.then(() => {
          unlockedAudioRef.current = a;
          setAudioUnlocked(true);
        }).catch(() => { /* политика ещё не позволяет */ });
      } else {
        unlockedAudioRef.current = a;
        setAudioUnlocked(true);
      }
    } catch (e) { /* noop */ }
  }, []);

  // Любая user-gesture на странице TV — пытаемся разблокировать звук.
  React.useEffect(() => {
    if (audioUnlocked) return;
    const onGesture = () => unlockAudio();
    window.addEventListener('click', onGesture);
    window.addEventListener('touchstart', onGesture, { passive: true });
    window.addEventListener('keydown', onGesture);
    return () => {
      window.removeEventListener('click', onGesture);
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, [audioUnlocked, unlockAudio]);

  // Проигрывание через РАЗБЛОКИРОВАННЫЙ Audio-элемент — переиспользуем
  // ссылку, чтобы браузер не считал каждый play() новым (потенциально
  // блокируемым) запросом.
  const playSoundUnlocked = React.useCallback((dataUrl) => {
    try {
      let a = unlockedAudioRef.current;
      if (!a) {
        // Fallback — пробуем сразу через новый Audio (на компе может работать).
        playAudioSafe(dataUrl);
        return;
      }
      a.pause();
      a.currentTime = 0;
      a.volume = 1.0;
      a.src = dataUrl;
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => { /* noop */ });
    } catch (e) { /* noop */ }
  }, []);

  React.useEffect(() => {
    if (!t) return;
    if (!soundEnabled) return;
    if (t.isPaused || !t.isRunning) return;
    const sounds = Array.isArray(t.sounds) ? t.sounds : [];
    sounds.forEach((s, idx) => {
      if (!s || !s.dataUrl) return;
      const key = s.id || `slot-${idx}`;
      const trigger = Math.max(0, Number(s.secondsBefore) || 0);
      if (remaining <= trigger && !playedSoundsRef.current.has(key)) {
        playedSoundsRef.current.add(key);
        playSoundUnlocked(s.dataUrl);
      }
    });
  }, [remaining, t, soundEnabled, playSoundUnlocked]);

  // Проигрывание звука банера при активации (enabled: false → true).
  // Играется один раз на каждую активацию, независимо от текста.
  const prevBannerEnabledRef = React.useRef(false);
  React.useEffect(() => {
    const isEnabled = !!(t?.banner && t.banner.enabled);
    if (isEnabled && !prevBannerEnabledRef.current && soundEnabled && t?.bannerSound?.dataUrl) {
      playSoundUnlocked(t.bannerSound.dataUrl);
    }
    prevBannerEnabledRef.current = isEnabled;
  }, [t?.banner?.enabled, t?.bannerSound?.dataUrl, soundEnabled, playSoundUnlocked, t]);

  // Автопереход уровня теперь делает сервер (ticker), чтобы не было гонок между
  // анонимными зрителями и админом. Клиент только визуально отсчитывает.

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (e) { /* noop */ }
  };

  // Синхронизируем локальный флаг fs с системным fullscreenchange (Esc и т.п.)
  React.useEffect(() => {
    const handler = () => setFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Скрываем UI-элементы и КУРСОР через 3с бездействия. Работает и вне
  // fullscreen (пользователь может держать вкладку с TV просто в браузере).
  // Курсор прячется через глобальный <style> с !important, чтобы дочерние
  // кнопки с `cursor-pointer` не переопределяли поведение.
  const [showFsControls, setShowFsControls] = React.useState(true);
  React.useEffect(() => {
    let timer = null;
    const reset = () => {
      setShowFsControls(true);
      clearTimeout(timer);
      timer = setTimeout(() => setShowFsControls(false), 3000);
    };
    reset();
    window.addEventListener('mousemove', reset);
    window.addEventListener('mousedown', reset);
    window.addEventListener('keydown', reset);
    window.addEventListener('touchstart', reset);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousemove', reset);
      window.removeEventListener('mousedown', reset);
      window.removeEventListener('keydown', reset);
      window.removeEventListener('touchstart', reset);
    };
  }, []);

  // Screen Wake Lock — не даём телевизору уходить в спящий режим, пока
  // открыты часы. При потере фокуса (visibilitychange) блок отпускается
  // системой автоматически; при возврате — переактивируем.
  React.useEffect(() => {
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;
    let sentinel = null;
    let released = false;
    const request = async () => {
      try {
        sentinel = await navigator.wakeLock.request('screen');
        sentinel.addEventListener('release', () => { sentinel = null; });
      } catch (e) { /* noop — на iOS Safari <16.4 и некоторых Android WebView может отсутствовать */ }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && !sentinel && !released) request();
    };
    request();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { sentinel && sentinel.release && sentinel.release(); } catch (e) { /* noop */ }
    };
  }, []);

  if (!t) {
    if (!notFound) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-neutral-950 text-white">
          <div className="text-center">
            <div className="text-2xl font-clock text-neutral-300 animate-pulse-soft">Загрузка…</div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950 text-white">
        <div className="text-center">
          <div className="text-6xl font-clock font-bold text-red-500">Часы не найдены</div>
          <div className="mt-4 text-neutral-400">Нет турнира с кодом {code}</div>
          <Button onClick={() => nav('/join')} className="mt-8 bg-red-600 text-white hover:bg-red-700">Другой код</Button>
        </div>
      </div>
    );
  }

  const level = t.blinds[t.currentLevelIndex];
  const nextLevel = t.blinds[Math.min(t.currentLevelIndex + 1, t.blinds.length - 1)];
  const isBreak = level?.isBreak;
  const isLastLevel = t.currentLevelIndex >= t.blinds.length - 1;
  const timeLow = remaining <= 3 && remaining > 0 && t.isRunning && !t.isPaused;
  const timeSoon = remaining <= 60 && remaining > 3 && t.isRunning && !t.isPaused;
  const untilBreak = secondsUntilBreak(t, remaining);
  const hasBg = !!t.backgroundImage;

  return (
    <div className={`fixed inset-0 flex items-center justify-center overflow-hidden bg-black ${!showFsControls ? 'cursor-none' : ''}`}>
      {!showFsControls && (
        <style>{`[data-testid="clock-root"], [data-testid="clock-root"] * { cursor: none !important; }`}</style>
      )}
      <div
        className={`relative overflow-hidden ${isBreak && !hasBg ? 'bg-gradient-to-br from-amber-950 to-neutral-950' : hasBg ? 'bg-black' : 'bg-neutral-950'} text-white ${timeLow ? 'flash-red' : ''}`}
        style={{
          width: 'min(100vw, calc(100vh * 16 / 9))',
          height: 'min(100vh, calc(100vw * 9 / 16))',
          containerType: 'size',
          ...(hasBg ? {
            backgroundImage: `url(${t.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {})
        }}
      >
      {/* Фон отображается в оригинальном виде — без затемнения */}

      {!fs && (
        <div className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-6 py-4">
          <Button variant="ghost" onClick={() => nav('/')} className="text-neutral-200 hover:bg-white/10 hover:text-white">
            <ArrowLeft className="mr-2 h-4 w-4" /> Выйти
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setSound(!soundEnabled)} className="text-neutral-200 hover:bg-white/10 hover:text-white">
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-neutral-200 hover:bg-white/10 hover:text-white">
              {fs ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      )}
      {fs && showFsControls && (
        <div className="absolute right-4 top-4 z-20 transition-opacity duration-500">
          <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-neutral-200/60 hover:bg-white/10 hover:text-white">
            <Minimize className="h-5 w-5" />
          </Button>
        </div>
      )}

      {/* Плашка «Пауза» рендерится как часть flex-контейнера таймера —
          см. ниже. Absolute-версия убрана. */}

      {/* Активация звука на TV: автоматически разблокируется первой
          user-интеракцией (click/touch/keydown) — visual-подсказка не нужна. */}

      {/* Баннер по центру ТВ — управляется из «Управление часами». Крупный
          жирный текст, выводится ПОВЕРХ всего контента (z-30). Тень + border
          для читаемости на любом фоне. Поддерживает многострочный ввод —
          каждая строка рендерится отдельным блоком, размер шрифта
          автоматически уменьшается в зависимости от кол-ва строк. */}
      {t.banner && t.banner.enabled && (t.banner.text || '').trim() && (() => {
        const lines = (t.banner.text || '').split('\n').filter((l) => l.trim().length > 0);
        // Первая строка — крупнее, остальные — чуть меньше. Всё масштабируется
        // от кол-ва строк, чтобы не выходить за пределы плашки.
        const primarySize = lines.length <= 1 ? 9 : lines.length === 2 ? 6.5 : 4.5;
        const secondarySize = lines.length === 2 ? 4 : 3.2;
        return (
          <div
            className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center px-8"

          >
            <div
              className="rounded-3xl border-4 border-red-500/60 bg-black/75 px-10 py-8 text-center shadow-2xl backdrop-blur-md"
              style={{ maxWidth: '90%' }}
            >
              {lines.map((ln, i) => (
                <div
                  key={i}
                  className="font-clock font-black uppercase leading-tight text-white drop-shadow-[0_4px_20px_rgba(239,68,68,0.55)]"
                  style={{
                    fontSize: `${i === 0 ? primarySize : secondarySize}cqw`,
                    letterSpacing: '0.03em',
                    wordBreak: 'break-word',
                    marginTop: i === 0 ? 0 : '0.6cqw',
                  }}
                >
                  {ln}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Боковая плашка слева: таймер до перерыва */}
      {untilBreak !== null && !isBreak && (
        <div className="absolute left-8 top-1/2 z-10 hidden -translate-y-1/2 md:block">
          <div className="rounded-3xl border border-white/10 bg-black/60 px-5 py-4 text-center shadow-2xl backdrop-blur">
            <div className="flex items-center justify-center gap-2 font-bold uppercase tracking-[0.2em] text-amber-400" style={{ fontSize: '1.3cqw' }}>
              <Coffee style={{ width: '1.3cqw', height: '1.3cqw' }} /> до перерыва
            </div>
            <div className="font-clock font-black text-amber-400" style={{ fontSize: '3.5cqw', fontVariantNumeric: 'tabular-nums', lineHeight: 1, marginTop: '0.3cqw' }}>
              <StaticDigits text={fmtLong(untilBreak)} />
            </div>
          </div>
        </div>
      )}

      {/* Боковая плашка справа: описание турнира. Показываем ВСЕГДА, если
          sideInfo не пусто — некоторые TV-браузеры отдают viewport 960px и
          не срабатывал прежний `lg:block` (>=1024). */}
      {t.sideInfo && t.sideInfo.trim().length > 0 && (
        <div className="absolute right-8 top-1/2 z-10 block -translate-y-1/2" style={{ width: '22cqw', maxWidth: '30rem' }}>
          <div className="rounded-3xl border border-red-500/30 bg-black/60 px-5 py-4 text-left shadow-2xl backdrop-blur">
            <div className="flex items-center gap-2 font-bold uppercase tracking-[0.2em] text-red-400" style={{ fontSize: '1.3cqw' }}>
              <Info style={{ width: '1.3cqw', height: '1.3cqw' }} /> Информация
            </div>
            <div className="mt-2 space-y-2 font-semibold text-neutral-100" style={{ fontSize: '1.5cqw', lineHeight: 1.35 }}>
              {t.sideInfo.split(/\n\n+/).map((para, i) => {
                const isTitle = i === 0 && para.length < 60 && !para.includes('\n');
                if (isTitle) {
                  return (
                    <div key={i} className="font-clock font-black uppercase tracking-wide text-white" style={{ fontSize: '1.9cqw', lineHeight: 1.15 }}>
                      {para}
                    </div>
                  );
                }
                return (
                  <p key={i} className="whitespace-pre-line text-neutral-100">{para}</p>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="relative z-10 grid h-full pt-3" style={{ gridTemplateRows: 'auto minmax(0, 1fr) auto' }}>
        <div className="px-8 text-center">
          <div className="font-clock font-black uppercase tracking-widest text-white drop-shadow-lg" style={{ fontSize: '4cqw' }}>{t.name}</div>
          {t.subtitle && <div className="mt-1 font-medium text-red-400 drop-shadow" style={{ fontSize: '2.4cqw' }}>{t.subtitle}</div>}
        </div>

        <div className="flex min-h-0 justify-center overflow-hidden px-4">
          <div className={`mx-auto flex h-full w-full flex-col text-center ${t.sideInfo && t.sideInfo.trim().length > 0 ? 'max-w-5xl' : 'max-w-6xl'}`}>
            <div className="font-clock font-black uppercase tracking-[0.35em] text-neutral-200 drop-shadow" style={{ fontSize: '2.4cqw' }}>
              {isBreak ? 'Перерыв' : `Уровень ${level?.level}`}
            </div>

            {isBreak ? (
              <div className="mt-6 font-clock font-black text-amber-300 drop-shadow-lg" style={{ fontSize: '8cqw' }}>
                {level.breakLabel || 'ПЕРЕРЫВ'}
              </div>
            ) : (
              <>
                {/* Grid 1fr | auto | 1fr => разделитель "|" всегда в центре.
                    Высота auto + отступы в cqw: при длинных блайндах
                    (миллион+) шрифт уменьшается, и фиксированные 12cqw
                    давали лишний воздух до Анте. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginTop: '3.5cqw',
                    marginBottom: '1cqw',
                  }}
                >
                  <div className="grid font-clock font-black leading-none text-white drop-shadow-lg" style={{ gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: '0.4em', fontSize: computeBlindsFontSize(level.sb, level.bb, !!(t.sideInfo && t.sideInfo.trim().length > 0)), fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                    <span style={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', height: '0.85em', lineHeight: 1 }}>{level.sb.toLocaleString('ru-RU')}</span>
                    <VDivider height="0.85em" thickness="0.07em" opacity={0.85} />
                    <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: '0.85em', lineHeight: 1 }}>{level.bb.toLocaleString('ru-RU')}</span>
                  </div>
                </div>
                {/* Строка ANTE рендерится только при ante > 0. Зазор до блайндов
                    держим компактным, чтобы ANTE читался как часть одного
                    блока ставок, но без слипания строк между собой. */}
                {level.ante > 0 && (
                  <div
                    className="grid font-extrabold uppercase tracking-wide text-amber-300 drop-shadow"
                    style={{ gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', columnGap: '0.5em', fontSize: '3.2cqw', lineHeight: 1, marginTop: '0.8cqw' }}
                  >
                    <span style={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', height: '0.85em' }}>Анте</span>
                    <VDivider height="0.7em" thickness="0.06em" opacity={0.8} />
                    <span style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', height: '0.85em' }}>{(level.ante || 0).toLocaleString('ru-RU')}</span>
                  </div>
                )}
              </>
            )}

            {/* Таймер + оверлей «Пауза» центрируются как пара в оставшемся
                пространстве между Анте и нижним блоком. Плашка «Пауза»
                накладывается ПОВЕРХ таймера, чтобы не залезать на нижний блок
                «Игроки / Следующие блайнды». */}
            <div className="flex flex-1 min-h-0 flex-col items-center justify-center">
              <div className="relative">
                <div
                  className={`leading-none drop-shadow-2xl ${timeLow ? 'text-red-500' : isBreak ? 'text-amber-300' : 'text-white'} ${timeSoon ? 'timer-glow-soft' : ''}`}
                  style={{
                    fontFamily: "'Oswald', 'Impact', system-ui, sans-serif",
                    fontSize: '14cqw',
                    fontWeight: 900,
                    fontVariantNumeric: 'tabular-nums',
                    fontFeatureSettings: '"tnum"',
                    letterSpacing: 0,
                  }}
                >
                  <StaticDigits text={fmt(remaining)} />
                </div>
                {/* Плашка «Пауза» — absolute поверх таймера. Только когда
                    isPaused, места не занимает, ничего не сдвигает. */}
                {t.isPaused && (
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 inline-flex items-center gap-4 rounded-full bg-red-600/90 font-semibold uppercase tracking-wider text-white shadow-2xl backdrop-blur"
                    style={{ fontSize: '1.8cqw', padding: '0.6cqw 2.4cqw' }}

                  >
                    <Pause style={{ width: '1.8cqw', height: '1.8cqw' }} /> Пауза
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-black/55 px-6 py-3 backdrop-blur">
          <div className="mx-auto grid max-w-7xl grid-cols-3 items-center gap-4 text-center">
            <div>
              <div className="font-bold uppercase tracking-widest text-neutral-300" style={{ fontSize: '1.275cqw' }}>Игроки</div>
              <div className="mt-1 font-clock font-black text-white" style={{ fontSize: '3.6cqw', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                {t.players.remaining}/{t.players.total}
              </div>
            </div>

            <div>
              {isLastLevel ? (
                <div className="invisible">—</div>
              ) : (
                <>
                  <div className="font-bold uppercase tracking-[0.25em] text-neutral-300" style={{ fontSize: '0.85cqw' }}>Следующие блайнды</div>
                  <div className="mt-1 font-clock font-black text-white drop-shadow" style={{ fontSize: nextLevel?.isBreak ? '4cqw' : computeNextBlindsFontSize(nextLevel?.sb, nextLevel?.bb), fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                    {nextLevel?.isBreak
                      ? (nextLevel.breakLabel || 'Перерыв').toUpperCase()
                      : (<span className="inline-flex items-center gap-4"><span style={{ display: 'inline-flex', alignItems: 'center', height: '0.85em' }}>{nextLevel?.sb.toLocaleString('ru-RU')}</span><VDivider height="0.85em" thickness="0.06em" opacity={0.85} /><span style={{ display: 'inline-flex', alignItems: 'center', height: '0.85em' }}>{nextLevel?.bb.toLocaleString('ru-RU')}</span></span>)}
                  </div>
                </>
              )}
            </div>

            {/* Правая колонка — пустое место для симметрии сетки (был "Средний стек"). */}
            <div aria-hidden="true" className="invisible">
              <div style={{ fontSize: '0.85cqw' }}>—</div>
              <div style={{ fontSize: '2.4cqw', lineHeight: 1 }}>—</div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
